import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { refreshStore } from "../lib/store";
import { useI18n } from "../lib/i18n";
import { DatePicker } from "../components/DatePicker";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Plus, X, Save } from "lucide-react";
import { OwnerPasswordCard } from "../components/OwnerPasswordCard";
import { StaffCard } from "../components/StaffCard";
import { TelegramCard } from "../components/TelegramCard";
import { PosterCard } from "../components/PosterCard";
import { BannersCard } from "../components/BannersCard";
import { StorefrontCard } from "../components/StorefrontCard";

export default function Pengaturan() {
  const { t, lang } = useI18n();
  const [s, setS] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [testing, setTesting] = useState(false);
  const dayNames = { id: 0, ja: 0, en: 0 };
  void dayNames;

  useEffect(() => {
    api.get("/settings").then(({ data }) => setS(data));
  }, []);

  if (!s) return <div className="p-8 text-muted-foreground">{t("loading")}</div>;

  const days = (
    {
      id: ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"],
      ja: ["日曜", "月曜", "火曜", "水曜", "木曜", "金曜", "土曜"],
      en: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    }[lang] || []
  );

  const setHour = (day, patch) =>
    setS({
      ...s,
      hours: s.hours.map((h) => (h.day === day ? { ...h, ...patch } : h)),
    });

  const save = async () => {
    try {
      const { data } = await api.put("/settings", {
        store_name: s.store_name,
        holiday_notice: s.holiday_notice,
        posters: s.posters || [],
        holiday_notice_ja: s.holiday_notice_ja,
        closed_dates: s.closed_dates,
        hours: s.hours,
        phone: s.phone,
        address: s.address,
        owner_email: s.owner_email,
        owner_whatsapp: s.owner_whatsapp,
        delivery_fee: s.delivery_fee,
        delivery_area: s.delivery_area,
        outside_fee: s.outside_fee,
        outside_area_label: s.outside_area_label,
        delivery_min_order: s.delivery_min_order,
        delivery_cutoff_hour: s.delivery_cutoff_hour,
        delivery_inside_hour: s.delivery_inside_hour,
        silver_spend: s.silver_spend,
        silver_pct: s.silver_pct,
        gold_spend: s.gold_spend,
        gold_pct: s.gold_pct,
        birthday_pct: s.birthday_pct,
        label_ad_text: s.label_ad_text,
        banners: s.banners || [],
        outside_cities: (s.outside_cities || []).filter((c) => c.city.trim()),
      });
      setS(data);
      await refreshStore();
      toast.success(t("saved"));
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan"));
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 w-full max-w-[1500px] 2xl:columns-2 2xl:gap-6 [&>div]:break-inside-avoid [&>div]:mb-6">
      <div>
        <h1 className="font-display text-3xl font-semibold">{t("settings_title")}</h1>
        <p className="text-sm text-muted-foreground">{t("settings_sub")}</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
        <p className="font-display font-semibold">{t("store_info")}</p>
        <Input
          data-testid="store-name-input"
          placeholder={t("store_name")}
          value={s.store_name || ""}
          onChange={(e) => setS({ ...s, store_name: e.target.value })}
        />
        <Input
          data-testid="store-address-input"
          placeholder={t("store_address")}
          value={s.address || ""}
          onChange={(e) => setS({ ...s, address: e.target.value })}
        />
        <div className="grid sm:grid-cols-2 gap-3">
          <Input
            data-testid="store-phone-input"
            placeholder={t("store_phone")}
            value={s.phone || ""}
            onChange={(e) => setS({ ...s, phone: e.target.value })}
          />
          <Input
            data-testid="owner-email-input"
            placeholder={t("owner_email")}
            value={s.owner_email || ""}
            onChange={(e) => setS({ ...s, owner_email: e.target.value })}
          />
          <Input
            data-testid="owner-whatsapp-input"
            placeholder={t("owner_whatsapp")}
            value={s.owner_whatsapp || ""}
            onChange={(e) => setS({ ...s, owner_whatsapp: e.target.value })}
          />
        </div>
        <Input
          data-testid="label-ad-text-input"
          placeholder={t("label_ad")}
          value={s.label_ad_text || ""}
          onChange={(e) => setS({ ...s, label_ad_text: e.target.value })}
        />
        <p className="text-xs text-muted-foreground">{t("label_ad_hint")}</p>
      </div>

      <OwnerPasswordCard />

      <StaffCard />

      <TelegramCard s={s} setS={setS} onSaved={refreshStore} />

      <StorefrontCard s={s} setS={setS} />
      <PosterCard s={s} setS={setS} />

      <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4" data-testid="outside-cities-manager">
        <div className="flex items-center justify-between gap-2">
          <p className="font-display font-semibold">{t("outside_cities")}</p>
          <Button
            size="sm"
            variant="outline"
            data-testid="add-city-btn"
            onClick={() =>
              setS({ ...s, outside_cities: [...(s.outside_cities || []), { city: "", fee: 1500 }] })
            }
          >
            <Plus size={15} className="mr-1" /> {t("add_city")}
          </Button>
        </div>
        {(s.outside_cities || []).length === 0 && (
          <p className="text-sm text-muted-foreground">{t("no_city")}</p>
        )}
        {(s.outside_cities || []).map((c, i) => (
          <div key={`city-row-${i}`} className="flex gap-2" data-testid={`city-row-${i}`}>
            <Input
              data-testid={`city-name-${i}`}
              placeholder={t("city_name")}
              value={c.city}
              onChange={(e) =>
                setS({
                  ...s,
                  outside_cities: s.outside_cities.map((x, j) =>
                    j === i ? { ...x, city: e.target.value } : x
                  ),
                })
              }
            />
            <Input
              data-testid={`city-fee-${i}`}
              type="number"
              className="w-32"
              placeholder={t("city_fee")}
              value={c.fee}
              onChange={(e) =>
                setS({
                  ...s,
                  outside_cities: s.outside_cities.map((x, j) =>
                    j === i ? { ...x, fee: Number(e.target.value) || 0 } : x
                  ),
                })
              }
            />
            <Button
              size="icon"
              variant="ghost"
              className="text-destructive shrink-0"
              data-testid={`city-delete-${i}`}
              onClick={() =>
                setS({ ...s, outside_cities: s.outside_cities.filter((_, j) => j !== i) })
              }
            >
              <X size={15} />
            </Button>
          </div>
        ))}
      </div>

      <BannersCard s={s} setS={setS} />

      <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
        <p className="font-display font-semibold">{t("business_hours")}</p>
        <div className="space-y-2" data-testid="hours-list">
          {s.hours.map((h) => (
            <div key={h.day} className="flex items-center gap-3 flex-wrap">
              <span className="w-24 text-sm">{days[h.day]}</span>
              <Switch
                data-testid={`day-open-${h.day}`}
                checked={!h.closed}
                onCheckedChange={(v) => setHour(h.day, { closed: !v })}
              />
              {h.closed ? (
                <span className="text-sm text-primary font-medium">{t("closed")}</span>
              ) : (
                <>
                  <Input
                    type="time"
                    data-testid={`open-time-${h.day}`}
                    value={h.open}
                    onChange={(e) => setHour(h.day, { open: e.target.value })}
                    className="h-9 w-32"
                  />
                  <span className="text-muted-foreground">–</span>
                  <Input
                    type="time"
                    data-testid={`close-time-${h.day}`}
                    value={h.close}
                    onChange={(e) => setHour(h.day, { close: e.target.value })}
                    className="h-9 w-32"
                  />
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
        <p className="font-display font-semibold">{t("holiday_notice")}</p>
        <Textarea
          data-testid="holiday-notice-input"
          rows={2}
          value={s.holiday_notice || ""}
          onChange={(e) => setS({ ...s, holiday_notice: e.target.value })}
        />
        <Textarea
          data-testid="holiday-notice-ja-input"
          rows={2}
          placeholder="日本語のお知らせ (catatan bahasa Jepang, opsional)"
          value={s.holiday_notice_ja || ""}
          onChange={(e) => setS({ ...s, holiday_notice_ja: e.target.value })}
        />
        <div className="flex gap-2 flex-wrap" data-testid="closed-dates">
          {(s.closed_dates || []).map((d) => (
            <span
              key={d}
              className="flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-3 py-1 text-sm"
            >
              {d}
              <button
                data-testid={`remove-closed-date-${d}`}
                onClick={() =>
                  setS({ ...s, closed_dates: s.closed_dates.filter((x) => x !== d) })
                }
              >
                <X size={13} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <DatePicker
            testid="new-closed-date-input"
            value={newDate}
            onChange={setNewDate}
            placeholder={t("closed_date")}
          />
          <Button
            variant="outline"
            data-testid="add-closed-date-btn"
            onClick={() => {
              if (newDate && !(s.closed_dates || []).includes(newDate)) {
                setS({ ...s, closed_dates: [...(s.closed_dates || []), newDate].sort() });
                setNewDate("");
              }
            }}
          >
            <Plus size={16} />
          </Button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
        <p className="font-display font-semibold">{t("delivery_fee")}</p>
        <p className="text-sm text-muted-foreground">
          {t("delivery_fee_note")} {s.delivery_area || "Kumamoto shi"}
        </p>
        <div className="flex gap-3 flex-wrap">
          <Input
            inputMode="numeric"
            data-testid="delivery-fee-input"
            value={s.delivery_fee ?? 800}
            onChange={(e) =>
              setS({ ...s, delivery_fee: Number(e.target.value.replace(/[^0-9]/g, "") || 0) })
            }
            className="h-10 w-32 num text-right"
          />
          <Input
            data-testid="delivery-area-input"
            placeholder={t("delivery_area")}
            value={s.delivery_area || ""}
            onChange={(e) => setS({ ...s, delivery_area: e.target.value })}
            className="h-10 w-56"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1">{t("outside_fee")}</p>
            <Input
              inputMode="numeric"
              data-testid="outside-fee-input"
              value={s.outside_fee ?? 1500}
              onChange={(e) =>
                setS({ ...s, outside_fee: Number(e.target.value.replace(/[^0-9]/g, "") || 0) })
              }
              className="h-10 num text-right"
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">{t("outside_label")}</p>
            <Input
              data-testid="outside-label-input"
              value={s.outside_area_label || ""}
              onChange={(e) => setS({ ...s, outside_area_label: e.target.value })}
              className="h-10"
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">{t("min_order")}</p>
            <Input
              inputMode="numeric"
              data-testid="min-order-input"
              value={s.delivery_min_order ?? 0}
              onChange={(e) =>
                setS({
                  ...s,
                  delivery_min_order: Number(e.target.value.replace(/[^0-9]/g, "") || 0),
                })
              }
              className="h-10 num text-right"
            />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1">
              Batas jam pesanan luar kota (0–23)
            </p>
            <Input
              inputMode="numeric"
              data-testid="delivery-cutoff-input"
              value={s.delivery_cutoff_hour ?? 15}
              onChange={(e) =>
                setS({
                  ...s,
                  delivery_cutoff_hour: Math.min(
                    23,
                    Number(e.target.value.replace(/[^0-9]/g, "") || 0)
                  ),
                })
              }
              className="h-10 num text-right"
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">
              Jam antar dalam Kumamoto shi (0–23)
            </p>
            <Input
              inputMode="numeric"
              data-testid="delivery-inside-hour-input"
              value={s.delivery_inside_hour ?? 21}
              onChange={(e) =>
                setS({
                  ...s,
                  delivery_inside_hour: Math.min(
                    23,
                    Number(e.target.value.replace(/[^0-9]/g, "") || 0)
                  ),
                })
              }
              className="h-10 num text-right"
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Pesanan dalam kota diantar setelah jam {s.delivery_inside_hour ?? 21}.00. Pesanan luar
          kota sebelum jam {s.delivery_cutoff_hour ?? 15}.00 dikirim hari yang sama, setelah itu
          dikirim keesokan hari. Jam ini juga dipakai pada pesan di keranjang, email, dan WhatsApp
          konfirmasi pembeli.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
        <p className="font-display font-semibold">{t("loyalty_settings")}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Silver ≥ (¥)</p>
            <Input
              inputMode="numeric"
              data-testid="silver-spend-input"
              value={s.silver_spend ?? 20000}
              onChange={(e) =>
                setS({ ...s, silver_spend: Number(e.target.value.replace(/[^0-9]/g, "") || 0) })
              }
              className="h-10 num text-right"
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Silver {t("discount")} (%)</p>
            <Input
              inputMode="numeric"
              data-testid="silver-pct-input"
              value={s.silver_pct ?? 5}
              onChange={(e) =>
                setS({ ...s, silver_pct: Number(e.target.value.replace(/[^0-9]/g, "") || 0) })
              }
              className="h-10 num text-right"
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Gold ≥ (¥)</p>
            <Input
              inputMode="numeric"
              data-testid="gold-spend-input"
              value={s.gold_spend ?? 50000}
              onChange={(e) =>
                setS({ ...s, gold_spend: Number(e.target.value.replace(/[^0-9]/g, "") || 0) })
              }
              className="h-10 num text-right"
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Gold {t("discount")} (%)</p>
            <Input
              inputMode="numeric"
              data-testid="gold-pct-input"
              value={s.gold_pct ?? 10}
              onChange={(e) =>
                setS({ ...s, gold_pct: Number(e.target.value.replace(/[^0-9]/g, "") || 0) })
              }
              className="h-10 num text-right"
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">{t("birthday_pct")}</p>
            <Input
              inputMode="numeric"
              data-testid="birthday-pct-input"
              value={s.birthday_pct ?? 15}
              onChange={(e) =>
                setS({ ...s, birthday_pct: Number(e.target.value.replace(/[^0-9]/g, "") || 0) })
              }
              className="h-10 num text-right"
            />
          </div>
        </div>
      </div>

      <Button className="h-12" data-testid="save-settings-btn" onClick={save}>
        <Save size={16} className="mr-2" /> {t("save_settings")}
      </Button>

      <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
        <p className="font-display font-semibold">{t("alert_channels")}</p>
        <p className="text-sm text-muted-foreground">
          In-app (lonceng) · Email: {s.owner_email || "-"} · WhatsApp: {s.owner_whatsapp || "-"}{" "}
          (aktif setelah kredensial Twilio diisi) · Rangkuman harian 21:00 JST
        </p>
        <p className="text-sm text-muted-foreground">
          {t("store_phone")}: {s.phone || "-"}
        </p>
        <Button
          variant="outline"
          data-testid="test-alert-btn"
          disabled={testing}
          onClick={async () => {
            setTesting(true);
            try {
              const { data } = await api.post("/alerts/test");
              toast.success(
                data.sent
                  ? `Alert terkirim (email: ${data.email ? "ya" : "gagal"}, WhatsApp: ${
                      data.whatsapp ? "ya" : "belum aktif"
                    })`
                  : data.reason || "Alert tidak terkirim"
              );
            } catch (e) {
              toast.error(errMsg(e, "Gagal mengirim alert uji"));
            } finally {
              setTesting(false);
            }
          }}
        >
          {t("test_alert")}
        </Button>
      </div>
    </div>
  );
}
